import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useScoringHistory } from '../hooks/useScoring.js';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';

/**
 * @graph-node {Page}
 * @graph-stack mall
 * @graph-domain Operations
 * @graph-business スコアリング履歴ページ。過去のスコアリング結果一覧をglassテーブルで表示。エラー時はフレンドリーな空状態メッセージを表示
 * @graph-connects useScoringHistory [calls] スコアリング履歴フック呼び出し
 */
export const Route = createFileRoute('/history')({
  component: HistoryPage,
});

/** @graph-connects none */
function HistoryPage() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useScoringHistory(50);

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            {t('history.title')}
          </h1>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
            {t('history.description')}
          </p>
        </div>
        <Card variant="glass">
          <div className="space-y-3">
            {Array.from({ length: 5 }, (_, i) => (
              <div
                key={i}
                className="flex items-center gap-4 py-3 border-b border-[var(--color-border)] last:border-b-0"
              >
                <div className="h-4 w-20 rounded bg-[var(--color-bg-hover)] skeleton-shimmer" />
                <div className="h-4 flex-1 rounded bg-[var(--color-bg-hover)] skeleton-shimmer" />
                <div className="h-8 w-8 rounded-full bg-[var(--color-bg-hover)] skeleton-shimmer" />
                <div className="h-4 w-10 rounded bg-[var(--color-bg-hover)] skeleton-shimmer" />
              </div>
            ))}
          </div>
        </Card>
      </div>
    );
  }

  // エラー時またはデータが空の場合はフレンドリーな空状態を表示
  const isEmpty = !!error || !data || data.results.length === 0;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
          {t('history.title')}
        </h1>
        <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
          {t('history.description')}
        </p>
      </div>

      {isEmpty && (
        <Card variant="glass" className="text-center py-12">
          <div className="flex flex-col items-center gap-4">
            <svg
              className="w-16 h-16 text-[var(--color-text-muted)]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-[var(--color-text-muted)]">{t('history.emptyDescription')}</p>
            <Link to="/">
              <Button size="md" variant="primary">
                {t('history.goToScoring')}
              </Button>
            </Link>
          </div>
        </Card>
      )}

      {!isEmpty && data && data.results.length > 0 && (
        <Card variant="glass">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[var(--color-text-muted)] text-xs border-b border-[var(--color-border)]">
                  <th className="text-left py-3 pr-4">{t('history.date')}</th>
                  <th className="text-left py-3 px-4">{t('history.product')}</th>
                  <th className="text-center py-3 px-4">{t('history.grade')}</th>
                  <th className="text-right py-3 pl-4">{t('history.score')}</th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((result) => (
                  <tr
                    key={result.id ?? result.timestamp}
                    className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)] transition-colors"
                  >
                    <td className="py-3 pr-4 text-[var(--color-text-secondary)] whitespace-nowrap">
                      {new Date(result.timestamp).toLocaleDateString('ja-JP')}
                    </td>
                    <td className="py-3 px-4">
                      {result.id ? (
                        <Link
                          to="/result/$id"
                          params={{ id: result.id }}
                          className="text-[var(--color-accent-primary)] hover:underline"
                        >
                          {result.productName}
                        </Link>
                      ) : (
                        <span className="text-[var(--color-text-primary)]">
                          {result.productName}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold"
                        style={{
                          color: result.gradeColor,
                          backgroundColor: result.gradeBg,
                        }}
                      >
                        {result.grade}
                      </span>
                    </td>
                    <td className="py-3 pl-4 text-right font-mono font-bold text-[var(--color-text-primary)]">
                      {result.total}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
